import { useEffect, useMemo } from "react";
import { Row, Col, Empty, Spin, Typography, Divider } from "antd";
import { useSelector, useDispatch } from "react-redux";
import DocumentCard from "../../../components/CustomCard/DocumentCard";
import { document as fetchDocuments } from "../../../redux/slices/DocumentSlice";
import { DESC_TXT } from "../../../constants/constants";

const { Text } = Typography;

const REQUIRED_FINANCIAL_DOCS = [
  {
    docMasterId: "DMS1000036",
    docMasterName: "financialSummary",
  },
  {
    docMasterId: "DMS1000047",
    docMasterName: "ratios",
  },
];

const FINANCIAL_DOC_IDS = [
  "DMS1000047", // Ratios
  "DMS1000036", // Financial Summary
];

const Documents = ({ entId }) => {
  const dispatch = useDispatch();
  const { docs, loading } = useSelector((state) => state.documents);

  useEffect(() => {
    const payload = { type: "ENTERPRISE", entId };
    dispatch(fetchDocuments(payload));
  }, [dispatch, entId]);

  const latestDocsByType = useMemo(() => {
    if (!Array.isArray(docs)) return [];

    const map = {};
    const transferOfRightsDocs = [];

    docs.forEach((doc) => {
      // Transfer of Rights → collect ALL
      if (doc.docMasterId === "DMS1000050") {
        transferOfRightsDocs.push(doc);
        return;
      }

      // Other documents → latest by modifiedAt
      const key = doc.docMasterId;
      const existing = map[key];

      if (
        !existing ||
        new Date(doc.modifiedAt) > new Date(existing.modifiedAt)
      ) {
        map[key] = doc;
      }
    });

    // Merge grouped docs + all transfer-of-rights docs
    return [...Object.values(map), ...transferOfRightsDocs];
  }, [docs]);

  const documentsToShow = useMemo(() => {
    const existingIds = new Set(latestDocsByType.map((doc) => doc.docMasterId));

    const missingDocs = REQUIRED_FINANCIAL_DOCS.filter(
      (doc) => !existingIds.has(doc.docMasterId),
    ).map((doc) => ({
      ...doc,
      isMissing: true,
    }));

    return [...latestDocsByType, ...missingDocs];
  }, [latestDocsByType]);

  const financialDocs = documentsToShow.filter((doc) =>
    FINANCIAL_DOC_IDS.includes(doc.docMasterId),
  );

  const enterpriseDocs = documentsToShow.filter(
    (doc) => !FINANCIAL_DOC_IDS.includes(doc.docMasterId),
  );

  const renderSection = (desc, data) => (
    <>
      <Text type="secondary">{desc}</Text>
      <Row gutter={[16, 16]} style={{ marginTop: 10 }} align="stretch">
        {data.map((doc) => (
          <Col
            xs={24}
            md={12}
            key={doc.docId || doc.docMasterId}
          >
            <DocumentCard doc={doc} docType="ENTERPRISE" entId={entId} />
          </Col>
        ))}
      </Row>
    </>
  );

  return (
    <Spin spinning={loading}>
      <div style={{ padding: 20 }}>
        {!documentsToShow.length ? (
          <Empty description="No Documents Found" />
        ) : (
          <>
            {financialDocs.length > 0 &&
              renderSection(DESC_TXT.UPLOAD_DOC_DESC + ".xlsx", financialDocs)}
            {financialDocs.length > 0 && enterpriseDocs.length > 0 && (
              <Divider />
            )}
            {enterpriseDocs.length > 0 &&
              renderSection(
                DESC_TXT.UPLOAD_DOC_DESC +
                  ".xlsx, .pdf, .zip, .doc, .docx, .png, .jpg, .jpeg",
                enterpriseDocs,
              )}
          </>
        )}
      </div>
    </Spin>
  );
};

export default Documents;
